import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Play, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface TrainingVideoModalProps {
  trigger: React.ReactNode;
  title?: string;
  locationKey?: string;
}

interface TrainingResource {
  id: string;
  title: string;
  description: string | null;
  file_url: string | null;
  embed_code: string | null;
  resource_type: string;
  is_active: boolean;
}

export const TrainingVideoModal = ({ trigger, title, locationKey }: TrainingVideoModalProps) => {
  const [trainingResource, setTrainingResource] = useState<TrainingResource | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchTrainingResource();
  }, [title, locationKey]);

  const fetchTrainingResource = async () => {
    const searchKey = locationKey || title;
    if (!searchKey) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('training_resources')
        .select('*')
        .eq('title', searchKey)
        .eq('is_active', true)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error fetching training resource:', error);
        return;
      }

      setTrainingResource(data);
    } catch (error) {
      console.error('Error fetching training resource:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayTitle = (): string | null => {
    if (!trainingResource?.description) return null;
    
    try {
      const parsed = JSON.parse(trainingResource.description);
      return parsed.displayTitle || null;
    } catch (e) {
      return null;
    }
  };

  const getDescription = (): string | null => {
    if (!trainingResource?.description) return null;
    
    try {
      const parsed = JSON.parse(trainingResource.description);
      return parsed.description || null;
    } catch (e) {
      // If parsing fails, return the raw description
      return trainingResource.description;
    }
  };

  const renderVideoContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!trainingResource) {
      return (
        <div className="text-center py-8">
          <Play className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-muted-foreground mb-4">No training video configured</p>
          <p className="text-sm text-muted-foreground">
            An administrator needs to configure the "{locationKey || title}" training resource.
          </p>
        </div>
      );
    }

    // If it's an embed code (Vimeo, YouTube, etc.)
    if (trainingResource.embed_code) {
      return (
        <div className="space-y-4">
          <div 
            className="w-full aspect-video rounded-lg overflow-hidden"
            dangerouslySetInnerHTML={{ __html: trainingResource.embed_code }}
          />
        </div>
      );
    }

    // If it's a direct file URL
    if (trainingResource.file_url) {
      const isVideoFile = trainingResource.file_url.match(/\.(mp4|webm|ogg)$/i);
      
      if (isVideoFile) {
        return (
          <div className="space-y-4">
            <video 
              controls 
              className="w-full aspect-video rounded-lg"
              src={trainingResource.file_url}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      } else {
        // It's a link to external content
        return (
          <div className="text-center py-8 space-y-4">
            <Play className="h-12 w-12 text-primary mx-auto mb-4" />
            <div>
              <h3 className="font-medium mb-2">{trainingResource.title}</h3>
            </div>
            <Button asChild>
              <a 
                href={trainingResource.file_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Open Training Video
              </a>
            </Button>
          </div>
        );
      }
    }

    return (
      <div className="text-center py-8">
        <Play className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <p className="text-muted-foreground">Training content not available</p>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="w-[70%] max-w-none">
        <DialogHeader>
          <DialogTitle>{getDisplayTitle() || title || "Training Video"}</DialogTitle>
          <DialogDescription>
            {getDescription() || "Training content to help you get started"}
          </DialogDescription>
        </DialogHeader>
        {renderVideoContent()}
      </DialogContent>
    </Dialog>
  );
};